import { useEffect, useContext, useState } from 'react';
import { Box } from '@mui/material';
import { EditOutlined, DeleteOutlineOutlined, ControlPointDuplicateOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import MaterialTable from '@material-table/core';
import { UserContext } from '../../contexts/UserContext';
import { generateClient } from 'aws-amplify/api';
import { enqueueSnackbar } from 'notistack';
import { deleteProfile, createAuditLog } from '../../graphql/mutations';
import { profileByTenant, listCampaigns } from '../../graphql/queries';
import { PageAppBar } from 'src/components/pageAppBar';
import { ConfirmDialog } from 'src/components/confirmDialog/confirmDialog';
import { checkCommunicationProfileUsage } from 'src/tools/checkUsage';

export function DialingProfiles() {
	const client = generateClient();
	const userContext = useContext(UserContext);
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const [profiles, setProfiles] = useState([]);
	const [toDelete, setToDelete] = useState(null);
	const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

	useEffect(() => {
		async function getData() {
			// Retrieving profiles
			setLoading(true);
			await getProfiles();
			setLoading(false);
		}
		if (userContext.tenantId) {
			getData();
		}
	}, [userContext.tenantId]);

	async function getProfiles() {
		let allProfiles = [];
		let nextToken;
		do {
			const options = {
				tenant: userContext.tenantId,
			};
			if (nextToken) {
				options.nextToken = nextToken;
			}
			const profiles = await client.graphql({
				query: profileByTenant,
				variables: options,
			});
			allProfiles = [...allProfiles, ...profiles.data.profileByTenant.items];
			nextToken = profiles.data.profileByTenant.nextToken;
		} while (nextToken);

		const sortedProfiles = allProfiles.sort((a, b) =>
			a.name.localeCompare(b.name),
		);
		setProfiles(sortedProfiles);
	}

	async function handleDelete() {
		try {
			setLoading(true);

			// Check if profile is in use by any active or paused campaign
			const isInUse = await checkCommunicationProfileUsage(toDelete, userContext.tenantId);

			if (isInUse.inUse) {
				enqueueSnackbar(isInUse.message, { variant: 'error' });
				setLoading(false);
				setToDelete(null);
				setConfirmDeleteOpen(false);
				return;
			}

			const campaignResult = await client.graphql({
				query: listCampaigns,
				variables: {
					filter: {
						and: [
							{ tenant: { eq: userContext.tenantId } },
							{ campaignProfileId: { eq: toDelete } },
						],
					},
				},
			});
			if (campaignResult.data.listCampaigns.items.length > 0) {
				enqueueSnackbar('Cannot delete the profile. Campaigns are using it.', {
					variant: 'error',
					autoHideDuration: 5000,
				});
				setLoading(false);
				setToDelete(null);
				setConfirmDeleteOpen(false);
			} else {
				await client.graphql({
					query: deleteProfile,
					variables: { input: { id: toDelete } },
				});
				await client.graphql({
					query: createAuditLog,
					variables: {
						input: {
							tenant: userContext.tenantId,
							type: 'Profile',
							timestamp: new Date().toISOString(),
							userName: userContext.email,
							documentId: toDelete,
							name: profiles.find(profile => profile.id === toDelete).name,
							currentValue: "{}",
							action: 'DELETED',
							ttl: Math.floor(Date.now() / 1000) + (6 * 30 * 24 * 60 * 60), //6 months
						}
					}
				});
				setToDelete(null);
				setConfirmDeleteOpen(false);
				enqueueSnackbar('Profile deleted successfully.', {
					variant: 'success',
					autoHideDuration: 2000,
				});
				await getProfiles();
				setLoading(false);
			}
		} catch (e) {
			console.error(e);
			enqueueSnackbar('Error deleting profile.', {
				variant: 'error',
				autoHideDuration: 2000,
			});
			setLoading(false);
			setToDelete(null);
			setConfirmDeleteOpen(false);
		}
	}

	function handleCancelDelete() {
		setToDelete(null);
		setConfirmDeleteOpen(false);
	}

	function handleSearchChange(value) {
		window.sessionStorage.setItem('ProfilesSearch', value);
	}

	return (
		<Box>
			<PageAppBar
				title="Communication Profiles"
				description="Set up Call Windows, Cadence, Attempts, and More"
				actionOneText={
					userContext.securityOptions.communicationProfiles > 1
						? '+ Profile'
						: ''
				}
				actionOneHandler={() => {
					navigate('/profiles/new');
				}}
				actionOneDisabled={
					userContext.securityOptions.communicationProfiles < 2
				}
			/>
			<MaterialTable
				components={{
					Container: (props) => <Box {...props} elevation={0} />,
				}}
				title=""
				data={profiles}
				columns={[
					{ title: 'Name', field: 'name' },
					{
						title: 'Channels',
						render: (rowData) => {
							const includedChannels = [];
							if (
								rowData.channels.find((channel) => channel.type === 'Voice')
							) {
								includedChannels.push('Voice');
							}
							if (rowData.channels.find((channel) => channel.type === 'SMS')) {
								includedChannels.push('SMS');
							}
							if (
								rowData.channels.find((channel) => channel.type === 'Email')
							) {
								includedChannels.push('Email');
							}

							return includedChannels.join(', ');
						},
					},
					{
						title: 'Voice Pacing',
						render: (rowData) => {
							if (
								rowData.channels.find((channel) => channel.type === 'Voice') &&
								rowData.clickToCall
							) {
								return 'Click to Call';
							} else if (
								rowData.channels.find((channel) => channel.type === 'Voice') &&
								rowData.previewDialing
							) {
								return 'Preview Dialing';
							} else if (
								rowData.channels.find((channel) => channel.type === 'Voice') &&
								rowData.overrideDialToAgentRatio
							) {
								return 'Static Dialing Ratio';
							} else if (
								rowData.channels.find((channel) => channel.type === 'Voice') &&
								rowData.thresholdDialing
							) {
								return 'Threshold Dialing';
							} else if (
								rowData.channels.find((channel) => channel.type === 'Voice')
							) {
								return 'Dynamic Dialing Ratio';
							} else {
								return '-';
							}
						},
					},
				]}
				isLoading={loading}
				options={{
					actionsColumnIndex: -1,
					pageSize: 10,
					searchText: window.sessionStorage.getItem('ProfilesSearch'),
					searchFieldStyle: {
						marginBottom: '16px',
						marginleft: '-28px',
					},
					headerStyle: {
						borderTop: '1px solid #e0e0e0',
						padding: '16px',
					},
					searchFieldVariant: 'outlined',
					paginationType: 'stepped',
				}}
				onSearchChange={handleSearchChange}
				actions={[
					{
						disabled: userContext.securityOptions.communicationProfiles < 2,
						icon: () => <EditOutlined color="primary" />,
						tooltip: 'Edit Profile',
						onClick: (event, rowData) => {
							navigate(`/profiles/${rowData.id}`);
						},
					},
					{
						disabled: userContext.securityOptions.communicationProfiles < 2,
						icon: () => <ControlPointDuplicateOutlined color="primary" />,
						tooltip: 'Clone Profile',
						onClick: (event, rowData) => {
							navigate(`/profiles/${rowData.id}?op=clone`);
						},
					},
					(rowData) =>
						(function () {
							const disabled =
								userContext.securityOptions.communicationProfiles < 2;
							return {
								icon: () => (
									<DeleteOutlineOutlined
										color={disabled ? 'disabled' : 'primary'}
									/>
								),
								tooltip: 'Delete Profile',
								onClick: (event, rowData) => {
									setToDelete(rowData.id);
									setConfirmDeleteOpen(true);
								},
								disabled,
							};
						})(),
				]}
				onRowClick={(event, rowData) => {
					navigate(`/profiles/${rowData.id}`);
				}}
			/>
			<ConfirmDialog
				open={confirmDeleteOpen}
				title="Delete Profile"
				description="Are you sure you want to delete this profile?"
				actionOneText="Delete"
				actionOneHandler={() => {
					handleDelete();
				}}
				actionTwoText="Cancel"
				actionTwoHandler={() => {
					handleCancelDelete();
				}}
			/>
		</Box>
	);
}
